import { TrashIcon, WarningIcon } from '@phosphor-icons/react';
import { CircleCheckIcon } from 'lucide-react';
import { FieldArray, FieldValues } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import { EventNumberField } from '@/modules/events/components/tabs/inventory-tab/components/event-number-field';
import { EventDetailsTab } from '@/modules/events/constants';
import { Routes } from '@/router/routes';

interface ModelSubColumnsProps {
  form: {
    update: (index: number, value: FieldArray<FieldValues, string>) => void;
  };
  onSetGcrDetails: (data: any) => void;
}

export const useModelSubColumns = ({
  form,
  onSetGcrDetails,
}: ModelSubColumnsProps) => {
  const navigate = useNavigate();
  const params = useParams<{ id: string; tab: EventDetailsTab }>();

  const redirectToPrinters = () => {
    navigate(`${Routes.Events}/${params.id}/${EventDetailsTab.Printers}`);
  };

  return [
    { accessorKey: 'subExpander' },
    { accessorKey: 'subName' },
    {
      accessorKey: 'subSize',
      cell: ({ row }) => row.original?.value,
    },
    {
      accessorKey: 'subQuantity',
      cell: ({ row }) => {
        const model = row.original.parent;
        const sizeId = row.original.sizeId;
        return (
          <EventNumberField
            defaultValue={String(row.original?.quantity ?? 0)}
            onChange={(newQuantity) => {
              const updatedSizes = model.sizes.map((size) =>
                size.sizeId === sizeId
                  ? { ...size, quantity: newQuantity }
                  : size
              );
              form.update(model.modelIndex, { ...model, sizes: updatedSizes });
            }}
          />
        );
      },
    },
    {
      accessorKey: 'subReserved',
      cell: ({ row }) => row.original?.reservedQuantity ?? 0,
    },
    { accessorKey: 'subPosition' },
    {
      accessorKey: 'subGcrFile',
      cell: ({ row }) => {
        if (!row.original.parent.modelId || !row.original?.sizeId) return '';
        const gcrFiles = row.original?.gcrFiles;

        switch (gcrFiles.action) {
          case 'redirect':
            return (
              <Button
                variant="link"
                className="text-destructive p-0"
                startIcon={<WarningIcon />}
                onClick={redirectToPrinters}
              >
                Please add or enable printers for this model
              </Button>
            );
          case 'create':
            return (
              <Button
                variant="link"
                className="text-destructive p-0"
                startIcon={<WarningIcon />}
                onClick={() =>
                  onSetGcrDetails({
                    typeId: gcrFiles.typeId,
                    sizeId: row.sizeId,
                    printerId: gcrFiles.printerId,
                    colorId: row.original.colorId,
                  })
                }
              >
                No GCR file for {gcrFiles.printerValue}
              </Button>
            );
          case 'none':
            return (
              <span>
                <CircleCheckIcon color={colors.green[400]} size={20} />
              </span>
            );
          case 'notNeeded':
            return <span className="normal-case text-zinc-400">Not Needed</span>;
          default:
            return null;
        }
      },
    },
    {
      accessorKey: 'subActions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            const model = row.original.parent;
            const sizes = model?.sizes || [];
            const sizeId = row.original.sizeId;

            const filteredSizes = sizes.filter(
              (size) => size.sizeId !== sizeId
            );

            form.update(model.modelIndex, {
              ...model,
              sizes: filteredSizes,
            });
          }}
          data-testid="remove-model-button"
        >
          <TrashIcon size={20} color={colors.zinc[400]} />
        </Button>
      ),
    },
  ];
};
